import { motion, useReducedMotion } from 'motion/react'
import type { BriefingWeekDay } from '../../api/types'
import { TOKENS } from '../ui/dashboard-primitives'

const AMBER = TOKENS.amber
const CARDIO_FILL = TOKENS.amberEmphasis
const EASE = [0.16, 1, 0.3, 1] as const
// eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
const TODAY_GLOW = '0 0 14px 2px rgba(232,197,160,0.28)'

function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/**
 * Seven dots, Mon-Sun, today ringed. A strength day fills amber, a cardio day a
 * dimmer amber, an empty day stays faint. The cheapest commitment device there
 * is (decision D8: the mascot reacts beside it).
 */
export function WeekStrip({ days }: { days: BriefingWeekDay[] }) {
  const todayISO = localISO(new Date())
  const reduceMotion = !!useReducedMotion()
  return (
    <div>
      <p
        className="text-[0.5625rem] tracking-[0.28em] uppercase"
        style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
      >
        This week
      </p>
      <div className="grid grid-cols-7 gap-1 mt-4">
        {days.map((d, i) => {
          const dt = new Date(`${d.date}T00:00`)
          const dow = dt.toLocaleDateString('en-US', { weekday: 'short' })
          const dom = String(dt.getDate()).padStart(2, '0')
          const isToday = d.date === todayISO
          const fill =
            d.kind === 'strength' ? AMBER : d.kind === 'cardio' ? CARDIO_FILL : null
          return (
            <div key={d.date} className="flex flex-col items-center gap-[11px]">
              <span
                className="text-[0.5625rem] tracking-[0.16em] uppercase"
                style={{
                  fontFamily: 'var(--font-mono)',
                  color: isToday ? AMBER : TOKENS.creamFaint,
                }}
              >
                {dow}
              </span>
              <span
                className="relative rounded-full"
                style={{
                  width: 14,
                  height: 14,
                  background: TOKENS.panelBorderDark,
                  outline: isToday ? '1px solid ' + TOKENS.amberEmphasis : undefined,
                  outlineOffset: isToday ? 3 : undefined,
                }}
              >
                {/* Today's ring breathes three times, then rests on a soft glow; the fill
                    scales in on reveal. It used to breathe forever, a 0.3 Hz loop on the first
                    screen that nothing could stop: the shadow is fixed and only its opacity
                    moves, so the pulse costs no repaint, and under Reduce Motion it holds still. */}
                {isToday && (
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 rounded-full"
                    style={{ boxShadow: TODAY_GLOW }}
                    initial={{ opacity: 0.5 }}
                    animate={reduceMotion ? { opacity: 0.5 } : { opacity: [0.5, 1, 0.5] }}
                    transition={reduceMotion ? { duration: 0 } : { duration: 3.4, repeat: 2, ease: 'easeInOut' }}
                  />
                )}
                {fill && (
                  <motion.span
                    className="absolute inset-0 rounded-full"
                    style={{ background: fill }}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.2 + i * 0.06 }}
                  />
                )}
              </span>
              <span
                className="text-[0.625rem] tabular-nums"
                style={{
                  fontFamily: 'var(--font-mono)',
                  color: isToday ? AMBER : TOKENS.creamDim,
                }}
              >
                {dom}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
